import { Controller } from "@hotwired/stimulus"

// Enable Stimulus debugging
if (typeof window !== 'undefined') {
  window.StimulusDebug = true
}

export default class extends Controller {
  static values = {
    groupId: String
  }

  // Define the highlight classes as a static property for easy maintenance
  static highlightClasses = [
    'relative',
    'after:absolute',
    'after:bottom-0',
    'after:left-0',
    'after:right-0',
    'after:h-[12px]',
    'after:bg-yellow-200/70',
    'dark:after:bg-yellow-500/40',
    'after:-z-10',
    'z-20',
    'highlight-transition'
  ]

  initialize() {
    // State management
    this.state = {
      hoverTimeout: null,
      hoverDelay: 250, // milliseconds delay before highlighting
      isHighlighted: false
    }

    this.boundHighlight = this.highlight.bind(this)
    this.logDebug("Initializing")
  }

  connect() {
    this.logDebug("Connected")
  }

  // Handle mouse enter with delay
  mouseEnter() {
    if (!this.isValidForHighlight()) return
    this.clearPendingTimeout()
    this.state.hoverTimeout = setTimeout(this.boundHighlight, this.state.hoverDelay)
  }

  // Handle mouse leave
  mouseLeave() {
    if (!this.isValidForHighlight()) return
    this.clearPendingTimeout()
    this.removeHighlight()
  }

  // Core highlighting logic
  highlight() {
    if (!this.isValidForHighlight()) return

    const relatedElements = this.findRelatedElements()
    if (!relatedElements.length) {
      this.logDebug("No related elements found", { groupId: this.groupIdValue })
      return
    }

    relatedElements.forEach(element => {
      element.classList.add(...this.constructor.highlightClasses)
    })
    this.state.isHighlighted = true
  }

  // Remove highlight from all related elements
  removeHighlight() {
    if (!this.isValidForHighlight()) return

    const relatedElements = this.findRelatedElements()
    relatedElements.forEach(element => {
      element.classList.remove(...this.constructor.highlightClasses)
    })
    this.state.isHighlighted = false
  }

  // Utility methods
  isValidForHighlight() {
    return this.hasGroupIdValue && this.groupIdValue
  }

  findRelatedElements() {
    const selector = `[data-highlight-group-group-id-value="${this.groupIdValue}"]`
    return document.querySelectorAll(selector)
  }

  clearPendingTimeout() {
    if (this.state.hoverTimeout) {
      clearTimeout(this.state.hoverTimeout)
      this.state.hoverTimeout = null
    }
  }

  // Debug logging
  logDebug(action, additionalData = {}) {
    return;
    console.group("🎯 Highlight Group Controller")
    console.log(action, {
      element: this.element,
      groupId: this.hasGroupIdValue ? this.groupIdValue : 'not set',
      state: this.state,
      ...additionalData
    })
    console.groupEnd()
  }

  // Lifecycle methods
  groupIdValueChanged(value, previousValue) {
    this.logDebug("Group ID changed", {
      from: previousValue,
      to: value,
      dataset: this.element.dataset,
      classList: Array.from(this.element.classList)
    })
  }

  disconnect() {
    this.clearPendingTimeout()
    if (this.state.isHighlighted) {
      this.removeHighlight()
    }
    this.logDebug("Disconnected", {
      classList: Array.from(this.element.classList)
    })
  }
}